import { isPlatformBrowser } from '@angular/common';
import {
  computed,
  effect,
  inject,
  Injectable,
  PLATFORM_ID,
  signal,
} from '@angular/core';

import { KEYCHAIN_NAME_ALLOWED_CHARACTERS_REGEX, MAX_UNITS_PER_CART_ITEM } from '@shared/constants/constants';

import { CartItem, CartItemCatalogUpdate } from './interfaces/cart.interfaces';

const MIN_CART_ITEM_QUANTITY = 1;
const MAX_CART_ITEM_QUANTITY = MAX_UNITS_PER_CART_ITEM;

@Injectable({
  providedIn: 'root',
})
export class CartService {
  public readonly cartItems = signal<CartItem[]>([]);
  public readonly isCartDrawerOpen = signal(false);

  private readonly platformId = inject(PLATFORM_ID);

  public readonly cartTotal = computed(() =>
    this.cartItems().reduce(
      (total, item) => total + item.unitPrice * item.quantity,
      0,
    ),
  );

  public readonly itemCount = computed(() =>
    this.cartItems().reduce(
      (count, item) => count + item.quantity,
      0,
    ),
  );

  constructor() {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.loadCartFromStorage();

    effect(() => {
      localStorage.setItem(
        'argentus_cart',
        JSON.stringify(this.cartItems()),
      );
    });
  }

  public openCart(): void {
    this.isCartDrawerOpen.set(true);
  }

  public closeCart(): void {
    this.isCartDrawerOpen.set(false);
  }

  public toggleCart(): void {
    this.isCartDrawerOpen.update(isOpen => !isOpen);
  }

  public addToCart(newItem: CartItem): void {
    this.cartItems.update(items => {
      const existingItem = items.find(item => item.id === newItem.id);

      if (!existingItem) {
        return [
          ...items,
          {
            ...newItem,
            quantity: this.clampQuantity(newItem.quantity),
          },
        ];
      }

      return items.map(item =>
        item.id === newItem.id
          ? {
            ...item,
            quantity: this.clampQuantity(item.quantity + newItem.quantity),
          }
          : item,
      );
    });

    this.openCart();
  }

  /**
   * Reemplaza nombres y precios con los del catálogo y saca las líneas que ya
   * no se pueden comprar. Se aplica en un solo `update` para no re-renderizar
   * el carrito una vez por línea.
   */
  public syncWithCatalog(updates: CartItemCatalogUpdate[]): void {
    const updateById = new Map(updates.map((update) => [update.id, update]));

    this.cartItems.update(items =>
      items
        .filter(item => updateById.get(item.id)?.available !== false)
        .map(item => {
          const update = updateById.get(item.id);

          if (!update) {
            return item;
          }

          return {
            ...item,
            name: update.name ?? item.name,
            unitPrice: update.unitPrice ?? item.unitPrice,
          };
        }),
    );
  }

  public removeByProductId(productId: string): CartItem[] {
    const removed = this.cartItems().filter(item => item.productId === productId);

    if (removed.length) {
      this.cartItems.update(items =>
        items.filter(item => item.productId !== productId),
      );
    }

    return removed;
  }

  public removeFromCart(itemId: string): void {
    this.cartItems.update(items =>
      items.filter(item => item.id !== itemId),
    );
  }

  public updateQuantity(itemId: string, newQuantity: number): void {
    const flooredQuantity = Math.floor(newQuantity);

    if (flooredQuantity <= 0) {
      this.removeFromCart(itemId);
      return;
    }

    const normalizedQuantity = this.clampQuantity(flooredQuantity);

    this.cartItems.update(items =>
      items.map(item =>
        item.id === itemId
          ? {
            ...item,
            quantity: normalizedQuantity,
          }
          : item,
      ),
    );
  }

  public clearCart(): void {
    this.cartItems.set([]);
  }

  private clampQuantity(quantity: number): number {
    const flooredQuantity = Math.floor(Number(quantity) || MIN_CART_ITEM_QUANTITY);

    return Math.min(
      MAX_CART_ITEM_QUANTITY,
      Math.max(MIN_CART_ITEM_QUANTITY, flooredQuantity),
    );
  }

  private loadCartFromStorage(): void {
    const savedCart = localStorage.getItem('argentus_cart');

    if (!savedCart) {
      return;
    }

    try {
      const parsedCart: unknown = JSON.parse(savedCart);

      if (!Array.isArray(parsedCart)) {
        throw new Error('El carrito almacenado no es una lista.');
      }

      this.cartItems.set(
        parsedCart
          .filter((item): item is CartItem => this.isValidCartItem(item))
          .map(item => ({
            ...item,
            quantity: this.clampQuantity(item.quantity),
            unitPrice: Math.max(0, Number(item.unitPrice) || 0),
          })),
      );
    } catch (error) {
      console.error(
        'Error al recuperar el carrito del localStorage',
        error,
      );

      localStorage.removeItem('argentus_cart');
    }
  }

  private isValidCartItem(item: unknown): item is CartItem {
    if (typeof item !== 'object' || item === null) {
      return false;
    }

    const candidate = item as Record<string, unknown>;

    return (
      typeof candidate['id'] === 'string' &&
      typeof candidate['productId'] === 'string' &&
      (candidate['productType'] === 'standard' || candidate['productType'] === 'keychain') &&
      typeof candidate['name'] === 'string' &&
      typeof candidate['unitPrice'] === 'number' &&
      Number.isFinite(candidate['unitPrice']) &&
      typeof candidate['quantity'] === 'number' &&
      (candidate['productType'] !== 'keychain' || this.hasValidKeychainName(candidate['variant']))
    );
  }

  private hasValidKeychainName(variant: unknown): boolean {
    if (typeof variant !== 'object' || variant === null) {
      return false;
    }

    const keychainName = (variant as Record<string, unknown>)['keychainName'];

    return typeof keychainName === 'string' && KEYCHAIN_NAME_ALLOWED_CHARACTERS_REGEX.test(keychainName.trim());
  }
}
